/**
 * stitch-toggle.js
 * ----------------------------------------------------------------------------
 * Handles the "Unstitch / Stitched" fake toggle added in variant-picker.liquid.
 *
 * - Uses event delegation on `document`, so it keeps working even if the
 *   theme's <product-rerender> re-fetches and swaps the variant-picker HTML
 *   after a variant change (no re-binding needed).
 * - Does NOT touch real Shopify variant selection logic. It only shows/hides
 *   the size row, and adds a `properties[Stitching Type]` hidden input so the
 *   cart line shows "Stitching Type: Stitched" (or "Unstitch").
 * - Clicking "Stitched" auto-selects the first available size so a real
 *   variant is active immediately and price updates right away. Add to Cart
 *   is only locked in the edge case where every size is sold out/disabled.
 *
 * Include this file after your theme's own JS, e.g. in theme.liquid:
 *   <script src="{{ 'stitch-toggle.js' | asset_url }}" defer></script>
 * ----------------------------------------------------------------------------
 */
(() => {
  const SELECT_SIZE_TEXT = 'Select a size';

  function getForm(picker) {
    const variantPicker = picker.closest('variant-picker');
    const formId = variantPicker?.getAttribute('form-id');
    return formId ? document.getElementById(formId) : null;
  }

  function getSubmitButton(form) {
    if (!form) return null;
    // Buy-buttons in this theme reference the form via a `form` attribute,
    // not necessarily by DOM nesting, so search the whole document.
    return document.querySelector(`[form="${form.id}"][type="submit"]`);
  }

  function setStitchProperty(picker, value) {
    const form = getForm(picker);
    if (!form) return;

    let input = document.querySelector(
      `input[name="properties[Stitching Type]"][form="${form.id}"]`
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Stitching Type]';
      input.setAttribute('form', form.id);
      picker.appendChild(input);
    }

    input.value = value;
  }

  function lockAddToCart(picker, locked) {
    const form = getForm(picker);
    const button = getSubmitButton(form);
    if (!button) return;

    if (locked) {
      if (!button.dataset.stitchOriginalText) {
        button.dataset.stitchOriginalText = button.textContent.trim();
      }
      button.disabled = true;
      button.setAttribute('data-stitch-locked', 'true');
      const label = button.querySelector('span') || button;
      label.textContent = SELECT_SIZE_TEXT;
    } else if (button.getAttribute('data-stitch-locked') === 'true') {
      button.disabled = false;
      button.removeAttribute('data-stitch-locked');
      const label = button.querySelector('span') || button;
      if (button.dataset.stitchOriginalText) {
        label.textContent = button.dataset.stitchOriginalText;
      }
    }
  }

  function showSizeRow(picker) {
    const sizeRow = picker.querySelector('[data-stitch-size-row]');
    const chargeNote = picker.querySelector('[data-stitch-charge-note]');
    const toggle = picker.querySelector('[data-stitch-toggle]');
    if (!sizeRow) return;

    sizeRow.hidden = false;
    if (chargeNote) chargeNote.hidden = false;
    toggle?.classList.add('is-active');
    toggle?.setAttribute('aria-pressed', 'true');

    // Auto-select the first available size so a real variant is active
    // immediately (this also fires the theme's own price-update logic).
    const firstAvailable = sizeRow.querySelector(
      'input[type="radio"]:not(:disabled)'
    );

    if (firstAvailable) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
      // The 'change' listener below handles setStitchProperty + unlocking ATC.
    } else {
      // Every size is sold out / disabled -- nothing valid to select.
      setStitchProperty(picker, 'Stitched');
      lockAddToCart(picker, true);
    }
  }

  function hideSizeRow(picker) {
    const sizeRow = picker.querySelector('[data-stitch-size-row]');
    const chargeNote = picker.querySelector('[data-stitch-charge-note]');
    const toggle = picker.querySelector('[data-stitch-toggle]');
    if (!sizeRow) return;

    sizeRow.hidden = true;
    if (chargeNote) chargeNote.hidden = true;
    toggle?.classList.remove('is-active');
    toggle?.setAttribute('aria-pressed', 'false');

    setStitchProperty(picker, 'Unstitch');
    lockAddToCart(picker, false);

    // Re-check the real "Unstitch" radio so price/variant state reverts.
    const unstitchInput = picker.querySelector(
      '[data-stitch-type-row] input[type="radio"]'
    );
    if (unstitchInput && !unstitchInput.checked) {
      unstitchInput.checked = true;
      unstitchInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Click on the fake "Stitched" button.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-stitch-toggle]');
    if (!toggle) return;

    const picker = toggle.closest('[data-stitch-picker]');
    if (!picker) return;

    event.preventDefault();
    showSizeRow(picker);
  });

  // A real radio changed inside a stitch-enabled picker: either the
  // "Unstitch" radio, or one of the actual size radios.
  document.addEventListener('change', (event) => {
    const input = event.target.closest(
      '[data-stitch-picker] input[type="radio"]'
    );
    if (!input) return;

    const picker = input.closest('[data-stitch-picker]');
    const inTypeRow = input.closest('[data-stitch-type-row]');

    if (inTypeRow) {
      // The real "Unstitch" radio was selected directly.
      hideSizeRow(picker);
    } else {
      // A real size radio was selected -> unlock Add to Cart.
      setStitchProperty(picker, 'Stitched');
      lockAddToCart(picker, false);
    }
  });
})();